<script lang="ts">
  import type { RaceSummary } from '../../types';
  import { app } from '../../lib/app.svelte';
  import { apIds, apPartial, colorVar, isCalled, leader, listText, pillText } from '../../lib/race';
  let { race }: { race: Pick<RaceSummary, 'status' | 'choices' | 'p_win' | 'projected_winners' | 'kind' | 'vote_for'> & { race_id?: string; ap_winners?: string[] | null } } = $props();
  const l = $derived(leader(race));
  const text = $derived(pillText(race));
  const called = $derived(isCalled(race));
  const settled = $derived(called || race.status === 'projected' || race.status === 'decided');
  const tone = $derived(settled ? 'solid' : race.status === 'leaning' && text !== 'Tossup' && race.vote_for === 1 ? 'tint' : 'plain');
  /** vote-for-N: some seats called by the AP, the model's status for the rest */
  const partial = $derived(apPartial(race));
  const partialTitle = $derived(partial ? `Called by the Associated Press: ${listText(apIds(race).map((id) => race.choices.find((c) => c.choice_id === id)?.name ?? id))}` : '');
  const c = $derived(l && tone !== 'plain' ? colorVar(l) : 'var(--ink-3)');
  /** the operator has paused this race's projection (meta.frozen_races) */
  const frozen = $derived(!!race.race_id && !!app.meta?.frozen_races?.includes(race.race_id));
</script>

<span class="wrap-p">
  <span class="pill {tone}" style="--c:{c}" title={called ? 'Called by the Associated Press' : undefined}>
    {#if settled}<svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.3l2.3 2.3L9.6 3.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>{/if}
    {#if race.status === 'counting' || race.status === 'leaning'}<i class="dot"></i>{/if}
    {text}
  </span>
  {#if partial}<span class="pill apc" title={partialTitle}>{partial} of {race.vote_for} called · AP</span>{/if}
  {#if frozen}<span class="pill frozen" title="The results desk has paused this projection while it checks the data; counts keep updating">Frozen</span>{/if}
</span>

<style>
  .wrap-p { display: inline-flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
  .pill { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; font: 700 0.72rem/1 var(--font); letter-spacing: 0.02em; padding: 6px 10px; border-radius: 999px; }
  .plain { color: var(--ink-2); background: var(--card-3); }
  /* text colours mixed toward the ink / black so every choice colour clears 4.5:1 in both themes */
  .tint { color: color-mix(in oklab, var(--c) 55%, var(--ink)); background: color-mix(in oklab, var(--c) 13%, var(--card)); }
  .solid { color: #fff; background: color-mix(in oklab, var(--c) 70%, #000); }
  .apc { color: var(--ink); background: var(--card); box-shadow: inset 0 0 0 1.5px var(--good); }
  .frozen { color: var(--ink); background: var(--card); box-shadow: inset 0 0 0 1.5px var(--ink-3); }
  .dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
</style>
